import { useState, useEffect } from 'react';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Bookmark,
  CheckCircle2,
  HelpCircle,
  Split,
  Calendar,
  Layers,
  Save,
} from 'lucide-react';
import Button from '@components/ui/Button.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import Input from '@components/ui/Input.js';
import { cn } from '@utils/Cn.js';
import { usePolicyTypesQuery } from '@features/policyTypes/index.js';
import type {
  FileIntrospectResult,
  ColumnMappingConfig,
  DefaultValuesConfig,
  ImportMappingPreset,
  TargetFieldKey,
} from '../types/index.js';

interface ColumnMappingWizardProps {
  introspectResult: FileIntrospectResult;
  mapping: ColumnMappingConfig;
  onMappingChange: (mapping: ColumnMappingConfig) => void;
  defaultValues: DefaultValuesConfig;
  onDefaultValuesChange: (defaults: DefaultValuesConfig) => void;
  onProceedToPreview: () => void;
  onBackToUpload: () => void;
  savedPresets: ImportMappingPreset[];
  onSavePreset: (name: string) => void;
  onLoadPreset: (preset: ImportMappingPreset) => void;
  isPreviewing: boolean;
}

interface TargetFieldDefinition {
  key: keyof ColumnMappingConfig;
  label: string;
  required: boolean;
  description: string;
}

const TARGET_FIELDS: TargetFieldDefinition[] = [
  {
    key: 'insuredName',
    label: 'Insured / Client Name',
    required: true,
    description: 'Name of the policyholder or primary business client',
  },
  {
    key: 'mobileNumber',
    label: 'Mobile Number',
    required: true,
    description: 'Primary 10-digit Indian contact number',
  },
  {
    key: 'endDate',
    label: 'Expiry / End Date',
    required: true,
    description: 'Policy expiration or renewal due date',
  },
  {
    key: 'policyTypeName',
    label: 'Policy Type',
    required: false,
    description: 'Category (Motor, Health, WC, Fire, etc.) or set via default below',
  },
  {
    key: 'policyNumber',
    label: 'Policy Number (Single Column)',
    required: false,
    description: 'Standard policy certificate number',
  },
  {
    key: 'vehicleNumber',
    label: 'Vehicle Number',
    required: false,
    description: 'Indian vehicle registration number (e.g. TN33AR0009)',
  },
  {
    key: 'referenceName',
    label: 'Reference Name',
    required: false,
    description: 'Referral person or source (e.g. Pradeep Gym, Mohan Tex)',
  },
  {
    key: 'premiumPrice',
    label: 'Premium Price',
    required: false,
    description: 'Annual premium amount in INR',
  },
  {
    key: 'referenceNote',
    label: 'Reference Note / Remarks',
    required: false,
    description: 'Vehicle models, coverage notes, or remarks',
  },
  {
    key: 'clientName',
    label: 'Associate / Parent Client Name',
    required: false,
    description: 'Optional parent client if this is a sub-account or group policy',
  },
  {
    key: 'outsourceAgentName',
    label: 'Outsource Agent Name',
    required: false,
    description: 'Name of external sub-agent or broker',
  },
  {
    key: 'outsourceAgentPhone',
    label: 'Outsource Agent Phone',
    required: false,
    description: 'Mobile number of external sub-agent',
  },
];

export default function ColumnMappingWizard({
  introspectResult,
  mapping,
  onMappingChange,
  defaultValues,
  onDefaultValuesChange,
  onProceedToPreview,
  onBackToUpload,
  savedPresets,
  onSavePreset,
  onLoadPreset,
  isPreviewing,
}: ColumnMappingWizardProps) {
  const [presetNameInput, setPresetNameInput] = useState('');
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);

  const { data: policyTypesResponse } = usePolicyTypesQuery({ limit: 100 });
  const availablePolicyTypes = policyTypesResponse?.data ?? [];

  const headers = introspectResult.headers;
  const sampleRows = introspectResult.sampleRows;
  const multiYearCols = introspectResult.detectedMultiYearColumns ?? [];

  // Distinct policy type strings detected in the file (from introspect or sample rows)
  const rawTypesInSheet = Array.from(
    new Set([
      ...(introspectResult.detectedPolicyTypes ?? []),
      ...sampleRows
        .map((r) => {
          const vehCol = mapping.vehicleNumber || introspectResult.headers.find((h) => h.toUpperCase().includes('VEH'));
          const ptCol = mapping.policyTypeName || introspectResult.headers.find((h) => h.toUpperCase().includes('TYPE'));
          return (vehCol ? r[vehCol] : '') || (ptCol ? r[ptCol] : '') || '';
        })
        .filter((v) => {
          if (!v) return false;
          const clean = v.trim().toUpperCase().replace(/[\s.-]/g, '');
          if (clean === 'NEW') return false;
          // Returns true if NOT an Indian vehicle registration
          return !/^[A-Z]{2}\d{1,2}[A-Z]{0,3}\d{1,4}$/.test(clean);
        }),
    ]),
  );

  // Helper to find the matching header in `headers` (exact, trimmed, case-insensitive, or alphanumeric)
  function findMatchingHeader(colName?: string): string | undefined {
    if (!colName) return undefined;
    if (headers.includes(colName)) return colName;
    const trimmed = colName.trim();
    const upper = trimmed.toUpperCase();
    const alpha = colName.toLowerCase().replace(/[^a-z0-9]/g, '');
    return (
      headers.find((h) => h === trimmed) ||
      headers.find((h) => h.toUpperCase() === upper) ||
      headers.find((h) => h.toLowerCase().replace(/[^a-z0-9]/g, '') === alpha)
    );
  }

  // Guarantee all detected columns are preselected into mapping on mount / change
  useEffect(() => {
    let changed = false;
    const updated: ColumnMappingConfig = { ...mapping };

    for (const field of TARGET_FIELDS) {
      const currentVal = updated[field.key] as string | undefined;
      const validCurrentHeader = findMatchingHeader(currentVal);

      if (validCurrentHeader && validCurrentHeader !== currentVal) {
        (updated as unknown as Record<string, string>)[field.key] = validCurrentHeader;
        changed = true;
      } else if (!validCurrentHeader) {
        const match = Object.entries(introspectResult.suggestedMapping).find(
          ([, s]) => s.targetKey === (field.key as TargetFieldKey) && s.confidence >= 50,
        );
        const resolvedMatch = match ? findMatchingHeader(match[0]) : undefined;
        if (resolvedMatch) {
          (updated as unknown as Record<string, string>)[field.key] = resolvedMatch;
          changed = true;
        }
      }
    }

    if (changed) {
      onMappingChange(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introspectResult, headers]);

  // Helper to extract first 3 non-empty values for any column
  function getSamplePreview(colName: string): string[] {
    if (!colName) return [];
    const samples: string[] = [];
    for (const row of sampleRows) {
      const val = row[colName];
      if (val && !samples.includes(val)) {
        samples.push(val);
        if (samples.length >= 3) break;
      }
    }
    return samples;
  }

  function handleFieldChange(fieldKey: keyof ColumnMappingConfig, colName: string) {
    const validHeader = colName === '__NONE__' ? undefined : (findMatchingHeader(colName) ?? colName);
    onMappingChange({
      ...mapping,
      [fieldKey]: validHeader,
    });
  }

  function handleToggleSplitRule() {
    onMappingChange({
      ...mapping,
      splitVehicleAndPolicyType: !mapping.splitVehicleAndPolicyType,
    });
  }

  function handleProceed() {
    // Ensure all resolved mappings are committed to parent state before proceeding
    const finalized: ColumnMappingConfig = { ...mapping };
    let hasChanges = false;

    for (const field of TARGET_FIELDS) {
      const currentVal = finalized[field.key] as string | undefined;
      const validHeader = findMatchingHeader(currentVal);
      if (validHeader) {
        if (validHeader !== currentVal) {
          (finalized as unknown as Record<string, string>)[field.key] = validHeader;
          hasChanges = true;
        }
      } else {
        const match = Object.entries(introspectResult.suggestedMapping).find(
          ([, s]) => s.targetKey === (field.key as TargetFieldKey) && s.confidence >= 50,
        );
        const resolved = match ? findMatchingHeader(match[0]) : undefined;
        if (resolved) {
          (finalized as unknown as Record<string, string>)[field.key] = resolved;
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      onMappingChange(finalized);
    }
    onProceedToPreview();
  }

  const isNameMapped = !!findMatchingHeader(mapping.insuredName);
  const isMobileMapped = !!findMatchingHeader(mapping.mobileNumber);
  const isDateMapped = !!findMatchingHeader(mapping.endDate);
  const isReadyToPreview = isNameMapped && isMobileMapped && isDateMapped;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Top Banner & Preset Controls */}
      <SurfaceCard className="p-6 border border-line/60 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-ink flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              Step 2: Map Spreadsheet Columns
            </h3>
            <p className="text-xs text-ink-faint mt-1">
              Match the columns from &ldquo;{introspectResult.fileName}&rdquo; to the system fields.
            </p>
          </div>

          {/* Preset Selector */}
          <div className="flex flex-wrap items-center gap-2">
            {savedPresets.length > 0 && (
              <select
                onChange={(e) => {
                  const p = savedPresets.find((item) => item.id === e.target.value);
                  if (p) onLoadPreset(p);
                }}
                defaultValue=""
                className="h-9 px-3 rounded-xl border border-line bg-paper text-xs font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-slate/20"
              >
                <option value="" disabled>
                  Load Saved Preset…
                </option>
                {savedPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}

            <Button
              variant="outline"
              size="sm"
              leftIcon={<Bookmark size={13} />}
              onClick={() => { setShowSavePresetDialog(!showSavePresetDialog); }}
              className="text-xs font-semibold"
            >
              Save as Preset
            </Button>
          </div>
        </div>

        {/* Save preset inline box */}
        {showSavePresetDialog && (
          <div className="rounded-xl border border-line bg-surface/50 p-4 space-y-3 animate-in fade-in duration-200">
            <p className="text-xs font-bold text-ink">Save this column mapping template</p>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. Client Monthly Master Tracker"
                value={presetNameInput}
                onChange={(e) => { setPresetNameInput(e.target.value); }}
                className="text-xs flex-1"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (presetNameInput.trim()) {
                    onSavePreset(presetNameInput.trim());
                    setShowSavePresetDialog(false);
                    setPresetNameInput('');
                  }
                }}
                leftIcon={<Save size={13} />}
                className="font-bold text-xs"
              >
                Save Preset
              </Button>
            </div>
          </div>
        )}
      </SurfaceCard>

      {/* ─── Smart Heuristic Rules Cards ────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Rule 1: Dual Vehicle / Policy Type Disambiguation */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggleSplitRule}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') handleToggleSplitRule();
          }}
          className={cn(
            'rounded-2xl border p-4 cursor-pointer transition-all text-left select-none',
            mapping.splitVehicleAndPolicyType
              ? 'border-green-500/40 bg-green-500/5 shadow-xs'
              : 'border-line/60 bg-surface/40 hover:border-line hover:bg-paper/40',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                  mapping.splitVehicleAndPolicyType
                    ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                    : 'bg-paper border border-line text-ink-faint',
                )}
              >
                <Split size={16} />
              </div>
              <div>
                <p className="text-xs font-bold text-ink">Smart Column Split: Vehicle vs. Policy Type</p>
                <p className="text-[11px] text-ink-faint mt-0.5 leading-snug">
                  If your &ldquo;Vehicle No&rdquo; column also contains non-motor types (Health, WC, Fire), they will be automatically separated!
                </p>
              </div>
            </div>
            <div
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs font-bold transition-all',
                mapping.splitVehicleAndPolicyType
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'border-line bg-paper text-transparent',
              )}
            >
              ✓
            </div>
          </div>
        </div>

        {/* Rule 2: Multi-Year Policy Renewal Tracking */}
        {multiYearCols.length > 0 && (
          <div className="rounded-2xl border border-purple-500/30 bg-purple-500/5 p-4 text-left">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-500/15 text-purple-600 dark:text-purple-400">
                <Calendar size={16} />
              </div>
              <div>
                <p className="text-xs font-bold text-ink">Multi-Year Policy Tracking ({multiYearCols.length} Years)</p>
                <p className="text-[11px] text-ink-faint leading-snug">
                  Automatically scans latest year first ({multiYearCols.join(' → ')}) to select active policy numbers.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {multiYearCols.map((y) => (
                <span
                  key={y}
                  className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-paper border border-purple-500/20 text-purple-700 dark:text-purple-300"
                >
                  {y}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Non-technical User Reassurance Banner ─── */}
      <div
        className={cn(
          'rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs transition-all',
          isReadyToPreview
            ? 'border-green-500/30 bg-green-500/10'
            : 'border-amber-500/30 bg-amber-500/5',
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
              isReadyToPreview
                ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                : 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
            )}
          >
            {isReadyToPreview ? <CheckCircle2 size={22} /> : <HelpCircle size={22} />}
          </div>
          <div>
            <h4 className="text-xs font-black text-ink flex items-center gap-1.5">
              <span>{isReadyToPreview ? 'Columns Automatically Preselected' : 'Required Column Mapping Needed'}</span>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-bold text-white',
                  isReadyToPreview ? 'bg-green-600' : 'bg-amber-600',
                )}
              >
                {isReadyToPreview ? 'Ready to Review' : 'Action Required'}
              </span>
            </h4>
            <p className="text-[11px] text-ink-faint mt-0.5">
              {isReadyToPreview
                ? 'Our intelligent engine has linked your spreadsheet columns. No manual mapping is required unless you want to make changes.'
                : 'Please map all required fields marked with * (Insured Name, Mobile Number, Expiry Date) before proceeding to review.'}
            </p>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleProceed}
          disabled={!isReadyToPreview || isPreviewing}
          rightIcon={<ArrowRight size={14} />}
          className={cn('shrink-0 font-bold shadow-sm', !isReadyToPreview && 'opacity-60 cursor-not-allowed')}
        >
          {isPreviewing ? 'Validating…' : 'Proceed to Review →'}
        </Button>
      </div>

      {/* ─── Column Mapping Table / Cards ────────────────────────────────────── */}
      <SurfaceCard className="p-6 border border-line/60 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-line/40">
          <h4 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
            <Layers size={14} className="text-slate" />
            Field Mapping Alignment
          </h4>
          <span className="text-xs text-ink-faint">
            <span className="text-red-500 font-bold">*</span> Required fields
          </span>
        </div>

        <div className="space-y-4 divide-y divide-line/30">
          {TARGET_FIELDS.map((field) => {
            const suggested = Object.entries(introspectResult.suggestedMapping).find(
              ([, s]) => s.targetKey === (field.key as TargetFieldKey),
            );
            const matchedSuggestedHeader = suggested ? findMatchingHeader(suggested[0]) : undefined;
            const currentVal = mapping[field.key] as string | undefined;
            const matchedCurrentHeader = findMatchingHeader(currentVal);
            const currentValue = matchedCurrentHeader || matchedSuggestedHeader || '';
            const sampleValues = getSamplePreview(currentValue);

            return (
              <div key={field.key} className="pt-4 first:pt-0">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                  {/* Left Column: Target Field Label & Description */}
                  <div className="min-w-0 md:w-5/12 space-y-1">
                    <p className="text-xs font-bold text-ink flex items-center gap-1.5">
                      {field.label}
                      {field.required && <span className="text-red-500 font-bold">*</span>}
                      {currentValue && headers.includes(currentValue) && (
                        <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                      )}
                    </p>
                    <p className="text-[11px] text-ink-faint leading-normal">
                      {field.description}
                    </p>

                    {/* Auto-suggested badge */}
                    {matchedSuggestedHeader && matchedSuggestedHeader === currentValue && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/10 text-green-700 dark:text-green-300">
                        <Sparkles size={10} />
                        Auto-matched ({suggested?.[1].confidence ?? 95}%)
                      </span>
                    )}
                  </div>

                  {/* Right Column: Source Column Dropdown & Sample Preview */}
                  <div className="md:w-7/12 space-y-2">
                    <select
                      value={currentValue || '__NONE__'}
                      onChange={(e) => { handleFieldChange(field.key, e.target.value); }}
                      className={cn(
                        'w-full h-10 px-3 rounded-xl border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-slate/20 transition-all',
                        currentValue && headers.includes(currentValue)
                          ? 'border-line-strong bg-paper text-ink shadow-2xs'
                          : field.required
                            ? 'border-red-500/40 bg-red-500/5 text-ink'
                            : 'border-line/70 bg-surface/40 text-ink-faint',
                      )}
                    >
                      <option value="__NONE__">
                        {field.required ? '— Select Column from File * —' : '— None / Not in File —'}
                      </option>
                      {headers.map((h) => {
                        const isAutoMatch = matchedSuggestedHeader === h;
                        return (
                          <option key={h} value={h}>
                            {h}{isAutoMatch ? ' ✨ (Auto-Matched)' : ''}
                          </option>
                        );
                      })}
                    </select>

                    {/* Sample preview chips */}
                    {sampleValues.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 pt-1">
                        <span className="text-[10px] font-bold text-ink-faint uppercase mr-1">
                          Sample data:
                        </span>
                        {sampleValues.map((sample, i) => (
                          <span
                            key={i}
                            className="inline-block max-w-[200px] truncate rounded-md border border-line/60 bg-paper px-2 py-0.5 font-mono text-[10px] text-ink"
                          >
                            {sample}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SurfaceCard>

      {/* ─── Policy Type Value Mapping & Alias Alignment ────────────────────── */}
      {rawTypesInSheet.length > 0 && (
        <SurfaceCard className="p-6 border border-line/60 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-line/40">
            <div>
              <h4 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles size={14} className="text-amber-500" />
                Policy Type &amp; Alias Value Alignment ({rawTypesInSheet.length} Types Found)
              </h4>
              <p className="text-[11px] text-ink-faint mt-0.5">
                Ensure abbreviations in your spreadsheet (e.g. &ldquo;WC&rdquo;, &ldquo;GHI&rdquo;, &ldquo;JB POLICY&rdquo;) map to the correct system policy types.
              </p>
            </div>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300">
              Auto-Matched via Aliases
            </span>
          </div>

          <div className="divide-y divide-line/30 space-y-3">
            {rawTypesInSheet.map((rawType) => {
              const upperRaw = rawType.trim().toUpperCase();
              const currentValue =
                mapping.policyTypeValueMappings?.[rawType] ??
                mapping.policyTypeValueMappings?.[upperRaw] ??
                '';

              // Determine matched system type
              const matchedSysType = availablePolicyTypes.find((pt) => {
                if (currentValue && (pt.name.toLowerCase() === currentValue.toLowerCase() || pt.id === currentValue)) return true;
                if (pt.name.toLowerCase() === rawType.toLowerCase()) return true;
                if (pt.aliases && Array.isArray(pt.aliases) && pt.aliases.some((a) => a.toLowerCase() === rawType.toLowerCase())) return true;
                // Standard built-in alias check
                if (upperRaw === 'WC' && (pt.name.includes("Workmen") || pt.name.includes("Workman"))) return true;
                if (upperRaw === 'GHI' && (pt.name.includes("Group") || pt.name.includes("Health"))) return true;
                if (upperRaw === 'JB POLICY' && pt.name.includes("Jewel")) return true;
                if (upperRaw === 'BURGLURY' && pt.name.includes("Burglary")) return true;
                return false;
              });

              const resolvedTargetName = currentValue || matchedSysType?.name || '';
              const isAutoMatched = !!matchedSysType && !currentValue;

              return (
                <div key={rawType} className="pt-3 first:pt-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-lg font-mono font-bold text-xs bg-paper border border-line text-ink shadow-2xs">
                      {rawType}
                    </span>
                    <ArrowRight size={13} className="text-ink-faint shrink-0" />
                    <span className="text-xs text-ink-soft">Maps to:</span>
                  </div>

                  <div className="flex items-center gap-2 flex-1 sm:max-w-xs">
                    <select
                      value={resolvedTargetName || '__CREATE_NEW__'}
                      onChange={(e) => {
                        const val = e.target.value;
                        const nextMappings = {
                          ...(mapping.policyTypeValueMappings ?? {}),
                          [rawType]: val === '__CREATE_NEW__' ? rawType : val,
                          [upperRaw]: val === '__CREATE_NEW__' ? rawType : val,
                        };
                        onMappingChange({
                          ...mapping,
                          policyTypeValueMappings: nextMappings,
                        });
                      }}
                      className={cn(
                        'w-full h-9 px-3 rounded-xl border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-slate/20 transition-all',
                        resolvedTargetName
                          ? 'border-green-500/40 bg-green-500/5 text-ink'
                          : 'border-line/70 bg-paper text-ink',
                      )}
                    >
                      <option value="">— Select Target System Policy Type —</option>
                      {availablePolicyTypes.map((pt) => (
                        <option key={pt.id} value={pt.name}>
                          {pt.name}
                        </option>
                      ))}
                      <option value="__CREATE_NEW__">
                        + Auto-create new &ldquo;{rawType}&rdquo; Policy Type
                      </option>
                    </select>

                    {isAutoMatched && (
                      <span className="shrink-0 text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-500/10 px-2 py-0.5 rounded-md">
                        ✨ Auto-Matched
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-ink-faint italic pt-1 border-t border-line/30">
            💡 Any newly confirmed mappings will be automatically remembered as persistent aliases in the system, so future monthly spreadsheets will recognize them immediately.
          </p>
        </SurfaceCard>
      )}

      {/* ─── Batch Default Values Section ────────────────────────────────────── */}
      <SurfaceCard className="p-6 border border-line/60 space-y-4">
        <h4 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
          <HelpCircle size={14} className="text-slate" />
          Default Values for Missing Fields
        </h4>
        <p className="text-xs text-ink-faint">
          If your spreadsheet does not have columns for certain required fields, you can assign a batch default here:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold text-ink-soft block mb-1">
              Default Policy Type
            </label>
            <Input
              placeholder="e.g. Motor or Health"
              value={defaultValues.policyTypeName ?? ''}
              onChange={(e) =>
                { onDefaultValuesChange({
                  ...defaultValues,
                  policyTypeName: e.target.value,
                }); }
              }
              className="text-xs"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-ink-soft block mb-1">
              Default Reference Name
            </label>
            <Input
              placeholder="e.g. Head Office / Direct"
              value={defaultValues.referenceName ?? ''}
              onChange={(e) =>
                { onDefaultValuesChange({
                  ...defaultValues,
                  referenceName: e.target.value,
                }); }
              }
              className="text-xs"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-ink-soft block mb-1">
              Default Premium Amount
            </label>
            <Input
              type="number"
              placeholder="e.g. 0"
              value={defaultValues.premiumPrice != null ? String(defaultValues.premiumPrice) : ''}
              onChange={(e) =>
                { onDefaultValuesChange({
                  ...defaultValues,
                  premiumPrice: e.target.value ? Number(e.target.value) : null,
                }); }
              }
              className="text-xs"
            />
          </div>
        </div>
      </SurfaceCard>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
        <Button
          variant="outline"
          size="md"
          onClick={onBackToUpload}
          leftIcon={<ArrowLeft size={15} />}
          className="w-full sm:w-auto font-semibold"
        >
          Back to Upload
        </Button>

        <Button
          variant="primary"
          size="md"
          disabled={!isReadyToPreview || isPreviewing}
          onClick={handleProceed}
          rightIcon={<ArrowRight size={15} />}
          className={cn('w-full sm:w-auto font-bold shadow-sm', !isReadyToPreview && 'opacity-60 cursor-not-allowed')}
        >
          {isPreviewing ? 'Validating…' : 'Proceed to Review →'}
        </Button>
      </div>
    </div>
  );
}
